import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import CustomLocalization from '../../../models/general/custom-localizations/custom-localization';
import CustomLocalizationType from '../../../models/enums/custom-localization-type';
import { useAppDispatch, useAppSelector } from '../../../store';
import { selectCustomLocalizations } from '../../../store/general/general-slice-selectors';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import { resetCustomLocalizationRestStatus } from '../../../store/general/general-slice';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import useValidatedInputEffect from '@common/hooks/useValidatedInputEffect';
import {
  createCustomLocalization,
  deleteCustomLocalizationById,
  patchCustomLocalizationById,
} from '../../../store/general/general-actions';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import { isHttpError } from '@common/interfaces/api/http-error';
import TextInput from '@common/components/Inputs/TextInput';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import CustomLocalizationCode from '../../../models/general/custom-localizations/custom-localization-code';
import {
  validateLocalizationCode,
  validateLocalizationDescription,
  validateLocalizationDescriptionKey,
  validateLocalizationKey,
  validateLocalizationTitle,
} from '../../../services/validation/custom-localization-validation';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import { AddIcon, DeleteIcon } from '@common/utils/icons/icons';
import CustomConfirmAbortDialog from '../../misc/CustomConfirmAbortDialog';

const CustomLocalizationForm: React.FC<{
  localization?: CustomLocalization;
  open: boolean;
  localizationType: CustomLocalizationType;
  onClose: () => void;
}> = ({ open, localization, localizationType, onClose }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const customLocalizations = useAppSelector((state) => selectCustomLocalizations(state));

  const { resetToggle, updateResetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      postError: customLocalizations.rest.postError,
      patchError: customLocalizations.rest.patchError,
      resetPostHandler: () =>
        dispatch(
          resetCustomLocalizationRestStatus({ postStatus: HttpFetchStatus.Idle, postError: null }),
        ),
      resetPatchHandler: () =>
        dispatch(
          resetCustomLocalizationRestStatus({
            patchStatus: HttpFetchStatus.Idle,
            patchError: null,
          }),
        ),
    });

  const [localType, setLocalType] = useState(localizationType);
  const [codes, setCodes] = useState<CustomLocalizationCode[]>(localization?.codes ?? []);
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (
      (customLocalizations.rest.postStatus === HttpFetchStatus.Success ||
        customLocalizations.rest.patchStatus === HttpFetchStatus.Success ||
        customLocalizations.rest.deleteStatus === HttpFetchStatus.Success) &&
      open
    ) {
      updateResetToggle();
      onClose();
    }
  }, [
    onClose,
    open,
    customLocalizations.rest.patchStatus,
    customLocalizations.rest.postStatus,
    updateResetToggle,
    customLocalizations.rest.deleteStatus,
  ]);

  useEffect(() => {
    if (localizationType !== localType) {
      setLocalType(localizationType);
      updateResetToggle();
    }
  }, [localType, localizationType, updateResetToggle]);

  const {
    value: title,
    validationResult: titleValidationResult,
    valueChangeHandler: titleChangeHandler,
  } = useValidatedInputEffect(
    localization?.title ?? '',
    (newValue) => validateLocalizationTitle(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('title'),
    removeServerError,
    resetToggle,
  );

  const {
    value: description,
    validationResult: descriptionValidationResult,
    valueChangeHandler: descriptionChangeHandler,
  } = useValidatedInputEffect(
    localization?.description ?? '',
    (newValue) => validateLocalizationDescription(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('description'),
    removeServerError,
    resetToggle,
  );

  const {
    value: titleKey,
    validationResult: titleKeyValidationResult,
    valueChangeHandler: titleKeyChangeHandler,
  } = useValidatedInputEffect(
    localization?.titleKey ?? '',
    (newValue) => validateLocalizationKey(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('titleKey'),
    removeServerError,
    resetToggle,
  );

  const {
    value: descriptionKey,
    validationResult: descriptionKeyValidationResult,
    valueChangeHandler: descriptionKeyChangeHandler,
  } = useValidatedInputEffect(
    localization?.descriptionKey ?? '',
    (newValue) => validateLocalizationDescriptionKey(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('descriptionKey'),
    removeServerError,
    resetToggle,
  );

  const {
    value: code,
    validationResult: codeValidationResult,
    valueChangeHandler: codeChangeHandler,
    setValue: setCodeHandler,
  } = useValidatedInputEffect(
    '',
    (newValue) => validateLocalizationCode(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('codes'),
    removeServerError,
    resetToggle,
  );

  const closeHandler = () => {
    dispatch(
      resetCustomLocalizationRestStatus({
        postStatus: HttpFetchStatus.Idle,
        postError: null,
        patchStatus: HttpFetchStatus.Idle,
        patchError: null,
        deleteStatus: HttpFetchStatus.Idle,
        deleteError: null,
      }),
    );
    updateResetToggle();
    setCodes([]);
    onClose();
  };

  const formSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    if (localization?.id != null) {
      dispatch(
        patchCustomLocalizationById({
          id: localization.id,
          data: {
            id: localization.id,
            title,
            description,
            titleKey,
            descriptionKey: descriptionKey.length === 0 ? null : descriptionKey,
            codes,
            type: localizationType,
          },
        }),
      );
    } else {
      dispatch(
        createCustomLocalization({
          title,
          description,
          titleKey,
          descriptionKey: descriptionKey.length === 0 ? null : descriptionKey,
          codes,
          type: localizationType,
        }),
      );
    }
  };

  const addCodeClickHandler = () => {
    if (code.length > 0 && !codes.some((c) => c.code === code)) {
      setCodes((prevState) => [...prevState, { code }]);
      setCodeHandler('');
    }
  };

  const deleteCodeClickHandler = (deletedCode: string) => {
    setCodes((prevState) => prevState.filter((c) => c.code !== deletedCode));
  };

  const openConfirmDeleteDialogClickHandler = () => {
    setIsConfirmDeleteDialogOpen(true);
  };

  const closeConfirmDeleteDialogHandler = () => {
    setIsConfirmDeleteDialogOpen(false);
  };

  const deleteLocalizationClickHandler = () => {
    if (localization?.id != null) {
      dispatch(deleteCustomLocalizationById(localization.id));
      setIsConfirmDeleteDialogOpen(false);
    }
  };

  return (
    <React.Fragment>
      <Dialog open={open} onClose={closeHandler} maxWidth="sm" fullWidth>
        <form onSubmit={formSubmitHandler}>
          <DialogTitle textAlign="center" variant="h2">
            {localization?.id != null
              ? title
              : t('title-create-custom-localization-' + localizationType)}
          </DialogTitle>
          <DialogContent>
            <Stack mt={2} spacing={2}>
              {customLocalizations.rest.postError !== null &&
                isHttpError(customLocalizations.rest.postError) && (
                  <CustomAlert
                    severity="error"
                    header={t(customLocalizations.rest.postError.message)}
                  />
                )}
              {customLocalizations.rest.patchError !== null &&
                isHttpError(customLocalizations.rest.patchError) && (
                  <CustomAlert
                    severity="error"
                    header={t(customLocalizations.rest.patchError.message)}
                  />
                )}
              <TextInput
                value={title}
                onChange={titleChangeHandler}
                validationResult={titleValidationResult}
                label={t('input-label-custom-localization-title')}
              />
              <TextInput
                value={description}
                onChange={descriptionChangeHandler}
                validationResult={descriptionValidationResult}
                label={t('input-label-custom-localization-description')}
              />
              <TextInput
                value={titleKey}
                onChange={titleKeyChangeHandler}
                validationResult={titleKeyValidationResult}
                label={t('input-label-custom-localization-key')}
              />
              <TextInput
                value={descriptionKey}
                onChange={descriptionKeyChangeHandler}
                validationResult={descriptionKeyValidationResult}
                label={t('input-label-custom-localization-description-key')}
              />
              <Stack mt={4}>
                <Typography variant="h2" mb={1}>
                  {t('title-localization-codes')}
                </Typography>
                {codes.length === 0 ? (
                  <CustomAlert severity="info" header={t('info-no-localization-codes')} />
                ) : (
                  codes.map((c) => (
                    <ListItem key={c.code} disablePadding>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                      >
                        <Typography ml={2} variant="body2">
                          {c.code}
                        </Typography>
                        <TooltipIconButton
                          icon={<DeleteIcon color="error" data-testid="delete-code-button" />}
                          onClick={() => deleteCodeClickHandler(c.code)}
                          tooltip={t('info-tooltip-delete')}
                          tooltipProps={{ placement: 'left' }}
                        />
                      </Stack>
                    </ListItem>
                  ))
                )}
                <TextInput
                  sx={{ mt: 2 }}
                  value={code}
                  onChange={codeChangeHandler}
                  validationResult={codeValidationResult}
                  label={t('input-label-custom-localization-code')}
                  endIcon={<AddIcon data-testid="add-code-button" />}
                  onEndIconClick={addCodeClickHandler}
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Stack direction="row" spacing={1}>
              <Button onClick={closeHandler}>{t('button-cancel')}</Button>
              <Button
                variant="contained"
                data-testid="button-add"
                disabled={
                  customLocalizations.rest.postStatus === HttpFetchStatus.Loading ||
                  customLocalizations.rest.patchStatus === HttpFetchStatus.Loading
                }
                type="submit"
              >
                {customLocalizations.rest.postStatus === HttpFetchStatus.Loading ||
                customLocalizations.rest.patchStatus === HttpFetchStatus.Loading ? (
                  <CircularProgress size={24} />
                ) : localization?.id == null ? (
                  t('button-add-localization')
                ) : (
                  t('button-save')
                )}
              </Button>
              {localization?.id != null && (
                <TooltipIconButton
                  icon={<DeleteIcon color="error" data-testid="delete-localization-button" />}
                  onClick={openConfirmDeleteDialogClickHandler}
                  tooltip={t('info-tooltip-delete')}
                />
              )}
            </Stack>
          </DialogActions>
        </form>
      </Dialog>
      <CustomConfirmAbortDialog
        open={isConfirmDeleteDialogOpen}
        title={t('title-delete-localization')}
        text={<CustomAlert severity="warning" header={t('warning-deleting-is-permanent')} />}
        confirmButton={t('button-delete')}
        abortButton={t('button-cancel')}
        onAbort={closeConfirmDeleteDialogHandler}
        onConfirm={deleteLocalizationClickHandler}
      />
      <CustomSnackbar
        open={customLocalizations.rest.postStatus === HttpFetchStatus.Success}
        title={t('success-creating-localization')}
        onClose={closeHandler}
      />
      <CustomSnackbar
        open={customLocalizations.rest.deleteStatus === HttpFetchStatus.Success}
        title={t('success-deleting-localization')}
        onClose={closeHandler}
      />
      <CustomSnackbar
        open={customLocalizations.rest.patchStatus === HttpFetchStatus.Success}
        title={t('success-patching-localization')}
        onClose={closeHandler}
      />
      <CustomSnackbar
        open={customLocalizations.rest.deleteStatus === HttpFetchStatus.Failed}
        title={t('error-deleting-localization')}
        onClose={closeHandler}
        severity="error"
        textMessages={
          customLocalizations.rest.deleteError != null &&
          isHttpError(customLocalizations.rest.deleteError)
            ? [customLocalizations.rest.deleteError.message]
            : undefined
        }
      />
    </React.Fragment>
  );
};

export default CustomLocalizationForm;
